import React, { useState, useEffect, useCallback } from "react";
import { AxiosError } from "axios";
import { IEventSummary, IEventHotel } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";

import { Modal, useModal } from "src/components/Modal/Modal";
import Button from "src/components/Button/Button";
import Confirm from "src/components/Confirm/Confirm";

import HotelTile from "./HotelTile";
import HotelForm from "./HotelForm";

import styles from "./HotelsTab.module.scss";

interface IProps {
  event: IEventSummary;
  isAdmin: boolean;
  onHotelsChange?: (hotels: IEventHotel[]) => void;
}

const HotelsTab: React.FC<IProps> = (props) => {
  const { isShown, toggle } = useModal();
  const [hotels, setHotels] = useState<IEventHotel[]>([]);
  const [editingHotel, setEditingHotel] = useState<IEventHotel | undefined>(undefined);
  const [deletingHotel, setDeletingHotel] = useState<IEventHotel | undefined>(undefined);
  const [deleting, setDeleting] = useState<boolean>(false);

  const getHotels = useCallback((id: string) => {
    apiService.getEventHotels(id).then((response: IEventHotel[]) => {
      setHotels(response);
      props.onHotelsChange?.(response);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getHotels(props.event._id);
  }, [props.event._id, getHotels]);

  const onAddClick = () => {
    setEditingHotel(undefined);
    toggle();
  };

  const onEditClick = (hotel: IEventHotel) => {
    setEditingHotel(hotel);
    toggle();
  };

  const onDeleteConfirm = () => {
    if (!deletingHotel) return;

    setDeleting(true);
    apiService.deleteEventHotel(props.event._id, deletingHotel._id).then(() => {
      toast.show({
        title: "Delete Hotel",
        content: `Successfully removed ${deletingHotel.name}`,
        duration: 8000,
        type: "success"
      });
      setDeletingHotel(undefined);
      getHotels(props.event._id);
    }).catch((e: AxiosError) => {
      toast.show({
        title: "Delete Hotel",
        content: "Unable to remove hotel",
        errorDetails: e,
        duration: 10000,
        type: "fail"
      });
    }).finally(() => {
      setDeleting(false);
    });
  };

  return (
    <div className={styles.HotelsTab}>
      {props.isAdmin && (
        <div className={styles.actions}>
          <div className={styles.left}></div>
          <div className={styles.right}>
            <Button onClick={onAddClick}>Add Hotel</Button>
          </div>
        </div>
      )}
      <div className={styles.list}>
        {hotels.length === 0 && <div className={styles.empty}>No hotels added yet.</div>}
        {hotels.map((hotel, index) => (
          <React.Fragment key={hotel._id}>
            {index > 0 && <div className={styles.divider}></div>}
            <HotelTile
              hotel={hotel}
              event={props.event}
              isAdmin={props.isAdmin}
              onEdit={onEditClick}
              onDelete={(h) => setDeletingHotel(h)}
            />
          </React.Fragment>
        ))}
      </div>
      {props.isAdmin && (
        <Modal
          className={editingHotel ? "editHotelModal" : "addHotelModal"}
          isShown={isShown}
          hide={() => { toggle(); }}
          modalContent={
            <HotelForm
              event={props.event}
              hotel={editingHotel}
              onFinish={() => { toggle(); getHotels(props.event._id); }}
              onCancel={() => { toggle(); }}
            />
          }
        />
      )}
      {props.isAdmin && (
        <Modal
          className="Confirmation"
          isShown={!!deletingHotel}
          hide={() => { setDeletingHotel(undefined); }}
          modalContent={
            <Confirm
              message={`Are you sure you want to remove ${deletingHotel?.name}?`}
              submitting={deleting}
              buttonsDisabled={deleting}
              onCancel={() => { setDeletingHotel(undefined); }}
              onConfirm={onDeleteConfirm}
            />
          }
        />
      )}
    </div>
  );
};

export default HotelsTab;
